import type { SecurityConfig } from './securityMiddleware';

/**
 * Security configuration for the e-commerce application
 * Adjust these settings based on your security requirements
 */

// Development configuration (more lenient)
const developmentConfig: SecurityConfig = {
  rateLimit: {
    windowMs: 60000, // 1 minute
    maxRequests: 200, // Allow more requests in development
    blockDurationMs: 60000, // Block for only 1 minute
  },
  loopDetection: {
    maxSameRequests: 20, // Allow more identical requests
    timeWindowMs: 15000, // 15 second window
  },
  sanitization: {
    allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS'],
    blockedPatterns: [
      /(<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>)/gi,
      /(javascript:|vbscript:|onload=|onerror=)/gi,
      /(union\s+select|drop\s+table|insert\s+into)/gi,
      /(\.\.\/|\.\.\\)/g,
      /(<iframe|<object|<embed)/gi,
    ],
    maxPayloadSize: 1024 * 1024 * 10, // 10MB for development
  },
};

// Production configuration (strict)
const productionConfig: SecurityConfig = {
  rateLimit: {
    windowMs: 60000, // 1 minute
    maxRequests: 60, // Stricter rate limiting
    blockDurationMs: 900000, // Block for 15 minutes
  },
  loopDetection: {
    maxSameRequests: 5, // Allow fewer identical requests
    timeWindowMs: 10000, // 10 second window
  },
  sanitization: {
    allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
    blockedPatterns: [
      // XSS prevention
      /(<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>)/gi,
      /(javascript:|vbscript:|data:text\/html|onload=|onerror=|onclick=|onmouseover=)/gi,
      
      // SQL injection prevention
      /(union\s+select|drop\s+table|insert\s+into|delete\s+from|update\s+.*set|create\s+table)/gi,
      /('|\"|;|--|\/\*|\*\/)/g,
      
      // Path traversal prevention
      /(\.\.\/|\.\.\\|%2e%2e%2f|%2e%2e%5c)/gi,
      
      // Command injection prevention
      /(\||&|;|`|\$\(|<\(|>\()/g,
      
      // File inclusion prevention
      /(file:\/\/|ftp:\/\/|php:\/\/|expect:\/\/)/gi,
      
      // Embedded content prevention
      /(<iframe|<object|<embed|<applet|<form|<link|<meta|<base)/gi,
      
      // Additional security patterns
      /(<svg|<math|<details|<summary)/gi,
      /(expression\s*\(|url\s*\(|@import)/gi,
    ],
    maxPayloadSize: 1024 * 1024 * 2, // 2MB max payload
  },
};

// E-commerce specific configuration
const ecommerceConfig: SecurityConfig = {
  rateLimit: {
    windowMs: 60000, // 1 minute
    maxRequests: 100, // Reasonable for shopping
    blockDurationMs: 300000, // Block for 5 minutes
  },
  loopDetection: {
    maxSameRequests: 10, // Allow product browsing
    timeWindowMs: 10000, // 10 second window
  },
  sanitization: {
    allowedMethods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'],
    blockedPatterns: [
      // All production patterns plus e-commerce specific
      ...productionConfig.sanitization.blockedPatterns,
      
      // E-commerce specific threats
      /(price\s*=\s*0|amount\s*=\s*0)/gi, // Price manipulation
      /(quantity\s*=\s*-?\d{4,})/gi, // Excessive quantities
      /(discount\s*=\s*1\d\d)/gi, // Excessive discounts
    ],
    maxPayloadSize: 1024 * 1024 * 5, // 5MB for product images
  },
};

/**
 * Get security configuration based on environment
 */
export const getSecurityConfig = (): SecurityConfig => {
  const env = process.env.NODE_ENV || 'development';
  
  switch (env) {
    case 'production':
      return productionConfig;
    case 'development':
      return developmentConfig;
    case 'test':
      return {
        ...developmentConfig,
        rateLimit: {
          ...developmentConfig.rateLimit,
          maxRequests: 1000, // Very lenient for testing
        },
      };
    default:
      return ecommerceConfig;
  }
};

/**
 * Override specific security settings
 */
export const createCustomSecurityConfig = (
  overrides: Partial<SecurityConfig>
): SecurityConfig => {
  const baseConfig = getSecurityConfig();
  return {
    ...baseConfig,
    ...overrides,
    rateLimit: {
      ...baseConfig.rateLimit,
      ...(overrides.rateLimit || {}),
    },
    loopDetection: {
      ...baseConfig.loopDetection,
      ...(overrides.loopDetection || {}),
    },
    sanitization: {
      ...baseConfig.sanitization,
      ...(overrides.sanitization || {}),
    },
  };
};

/**
 * Security configuration for different API endpoints
 */
export const endpointSpecificConfigs = {
  auth: createCustomSecurityConfig({
    rateLimit: {
      windowMs: 60000,
      maxRequests: 5, // Very strict for auth endpoints
      blockDurationMs: 900000, // 15 minutes
    },
  }),
  
  search: createCustomSecurityConfig({
    rateLimit: {
      windowMs: 60000,
      maxRequests: 30, // Moderate for search
      blockDurationMs: 60000, // 1 minute
    },
  }),
  
  checkout: createCustomSecurityConfig({
    rateLimit: {
      windowMs: 60000,
      maxRequests: 10, // Strict for checkout
      blockDurationMs: 600000, // 10 minutes
    },
  }),
  
  wishlist: createCustomSecurityConfig({
    rateLimit: {
      windowMs: 60000,
      maxRequests: 50, // Moderate for wishlist operations
      blockDurationMs: 120000, // 2 minutes
    },
  }),
};

export default getSecurityConfig;
